import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import io from 'socket.io-client';
import { getConfig } from './config/runtime-config';
import Navbar from './components/Navbar';
import Loading from './components/Loading';
import ErrorBoundary from './components/ErrorBoundary';
import useUserSetup from './hooks/useUserSetup';
import UserContext from './contexts/UserContext';
import { NotificationProvider, useNotification } from './contexts/NotificationContext';
import NotificationModal from './components/NotificationModal';
import { setGlobalNotificationHandler, setSessionExpiredHandler } from './services/api';
import log from './utils/logger';

// Lazy-loaded page components (code-split per route)
const Home = lazy(() => import('./pages/Home'));
const Orders = lazy(() => import('./pages/Orders'));
const Settings = lazy(() => import('./pages/Settings'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Payment = lazy(() => import('./pages/Payment'));
const HistoricalAuctions = lazy(() => import('./pages/HistoricalAuctions'));
const MyAuctions = lazy(() => import('./pages/MyAuctions'));
const EditAuction = lazy(() => import('./pages/EditAuction'));
const TestFlow = lazy(() => import('./pages/TestFlow'));
const AuctionDetail = lazy(() => import('./pages/AuctionDetail'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const NotificationList = lazy(() => import('./pages/NotificationList'));

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth0();

  if (isLoading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
    return <Navigate to="/" replace />;
  }

  return children;
};

const VerifiedRoute = ({ children, emailVerified }) => {
  const { isAuthenticated, isLoading } = useAuth0();

  if (isLoading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
    return <Navigate to="/" replace />;
  }

  if (!emailVerified) {
    return <Navigate to="/settings" replace />;
  }

  return children;
};

const AppContent = () => {
  const { isLoading, error, loginWithRedirect } = useAuth0();
  const { loading: userSetupLoading, emailVerified, setEmailVerified } = useUserSetup();
  const [socket, setSocket] = useState(null);
  const { showNotification } = useNotification();

  // Set up global notification handler for API errors
  useEffect(() => {
    setGlobalNotificationHandler(showNotification);
  }, [showNotification]);

  // Set up session expired handler to redirect to login
  useEffect(() => {
    setSessionExpiredHandler(() => loginWithRedirect());
  }, [loginWithRedirect]);

  useEffect(() => {
    // Get Socket.IO URL from runtime config
    const config = getConfig();
    const socketUrl = config.socketUrl || 'http://localhost:5000';
    
    const socketConnection = io(socketUrl);
    
    socketConnection.on('connect_error', (error) => {
      log.error('Socket connection error:', error);
    });
    
    setSocket(socketConnection);
    // Store globally for cleanup
    window.socket = socketConnection;

    return () => {
      socketConnection.disconnect();
      window.socket = null;
    };
  }, []);

  if (error) {
    if (error.message?.includes('Invalid state') || error.error === 'login_required') {
      loginWithRedirect();
      return <Loading />;
    }
    return (
      <div className="min-h-screen flex items-center justify-center" role="alert">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Authentication Error</h1>
          <p className="text-gray-600">{error.message}</p>
          <button 
            onClick={() => loginWithRedirect()} 
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (isLoading || userSetupLoading) {
    return <Loading />;
  }

  return (
    <UserContext.Provider value={{ emailVerified, setEmailVerified }}>
      <Router>
      <div className="min-h-screen bg-dark-bg flex flex-col">
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:p-4 focus:bg-snipehouse-blue focus:text-white">Skip to main content</a>
        <Navbar socket={socket} />
        <main id="main-content" className="flex-1 bg-dark-bg">
          <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/" element={<Home socket={socket} />} />
            <Route path="/payment/:auctionId" element={
              <VerifiedRoute emailVerified={emailVerified}>
                <Payment />
              </VerifiedRoute>
            } />
            <Route path="/orders" element={
              <VerifiedRoute emailVerified={emailVerified}>
                <Orders />
              </VerifiedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <Settings emailVerified={emailVerified} />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <VerifiedRoute emailVerified={emailVerified}>
                <Dashboard />
              </VerifiedRoute>
            } />
            <Route path="/historical" element={<HistoricalAuctions />} />
            <Route path="/my-auctions" element={
              <VerifiedRoute emailVerified={emailVerified}>
                <MyAuctions />
              </VerifiedRoute>
            } />
            <Route path="/edit-auction/:id" element={
              <VerifiedRoute emailVerified={emailVerified}>
                <EditAuction />
              </VerifiedRoute>
            } />
            <Route path="/auction/:id" element={<AuctionDetail />} />
            <Route path="/order/:orderId" element={
              <VerifiedRoute emailVerified={emailVerified}>
                <OrderDetail />
              </VerifiedRoute>
            } />
            <Route path="/notifications" element={
              <VerifiedRoute emailVerified={emailVerified}>
                <NotificationList />
              </VerifiedRoute>
            } />
            {process.env.NODE_ENV !== 'production' && (
              <Route path="/test-flow" element={<TestFlow />} />
            )}
          </Routes>
          </Suspense>
        </main>
        <NotificationModal />
      </div>
      </Router>
    </UserContext.Provider>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <NotificationProvider>
        <AppContent />
      </NotificationProvider>
    </ErrorBoundary>
  );
};

export default App;
